import { createFileRoute } from "@tanstack/react-router";
import { AppWindow, PiggyBank, FileCheck, ExternalLink } from "lucide-react";

import { SectionHeading } from "../components/SectionHeading";
import { CtaBand } from "../components/CtaBand";
import { SITE_URL, ogImageDefault, ogImageMeta } from "../lib/seo";

const subsidieImage =
  "/__l5e/assets-v1/4fbb1e01-6bc8-488c-afed-4272c4741e40/subsidie-isoleren.webp";
const subsidieImage768 =
  "/__l5e/assets-v1/4780aeb3-1fad-493c-900e-3171bf5b260a/subsidie-isoleren-768-v2.webp";

export const Route = createFileRoute("/subsidie")({
  head: () => ({
    meta: [
      { title: "Subsidie Dordt Isoleert — Fedor Timmerwerken" },
      {
        name: "description",
        content:
          "Nieuwe kozijnen en isolerend glas met subsidie van Dordt Isoleert. Fedor Timmerwerken levert subsidieproof offertes en facturen voor je aanvraag.",
      },
      {
        property: "og:title",
        content: "Subsidie Dordt Isoleert — Fedor Timmerwerken",
      },
      {
        property: "og:description",
        content:
          "Nieuwe kozijnen en isolerend glas met subsidie van Dordt Isoleert, met subsidieproof offertes en facturen.",
      },
      { property: "og:url", content: `${SITE_URL}/subsidie` },
      ...ogImageMeta(ogImageDefault, "Isolerende maatregelen met subsidie — Fedor Timmerwerken"),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/subsidie` }],
  }),
  component: SubsidiePage,
});

const punten = [
  {
    title: "Glas en kozijnen",
    description:
      "Nieuwe kozijnen en goed isolerend glas zorgen voor meer wooncomfort, een lagere energierekening en een frisse uitstraling van je woning. Wij leveren en plaatsen kunststof, houten én aluminium kozijnen — altijd vakkundig ingemeten en afgewerkt.",
    icon: AppWindow,
  },
  {
    title: "Subsidie mogelijk",
    description:
      "Ga je je woning verduurzamen? Dan kom je mogelijk in aanmerking voor een subsidie voor isolerende maatregelen, zoals HR++ of triple glas in combinatie met nieuwe kozijnen. Bekijk de voorwaarden via Dordt Isoleert of informeer naar de mogelijkheden.",
    icon: PiggyBank,
  },
  {
    title: "Subsidieproof offertes en facturen",
    description:
      "Wij houden rekening met de eisen die gelden voor subsidieaanvragen. Onze offertes en facturen bevatten de benodigde informatie, zodat je ze direct kunt gebruiken bij je aanvraag en alles zo soepel mogelijk verloopt.",
    icon: FileCheck,
  },
];

function SubsidiePage() {
  return (
    <>
      <div className="py-14 sm:py-20 lg:py-28">
        <div className="container-tight">
          <SectionHeading
            as="h1"
            eyebrow="Subsidie"
            title="Profiteren van de subsidie van Dordt Isoleert?"
            description="Fedor Timmerwerken plaatst nieuwe kozijnen en isolerend glas én levert offertes en facturen die geschikt zijn voor je subsidieaanvraag. Zo ben je verzekerd van vakwerk én een goede basis voor het aanvragen van subsidie."
            align="center"
            className="mx-auto"
          />

          <div className="mt-10 sm:mt-14 grid gap-10 lg:grid-cols-2 lg:items-start">
            <div className="overflow-hidden rounded-2xl border border-border/60 lg:sticky lg:top-24">
              <img
                src={subsidieImage}
                srcSet={`${subsidieImage768} 768w, ${subsidieImage} 1200w`}
                sizes="(min-width: 1024px) 50vw, calc(100vw - 2rem)"
                alt="Een zolder isoleren — voorbeeld van een subsidiabele maatregel"
                width={1200}
                height={466}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>

            <div className="space-y-6">
              {punten.map((punt) => (
                <div key={punt.title} className="rounded-xl border border-border/60 bg-card p-6">
                  <div className="flex items-center gap-4">
                    <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <punt.icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <h2 className="font-heading text-lg font-semibold text-foreground">
                      {punt.title}
                    </h2>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {punt.description}
                  </p>
                </div>
              ))}

              <a
                href="https://energiehulp.nl/dordrecht/isoleren/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between gap-4 rounded-xl border border-primary/30 bg-primary/5 p-6 transition-colors hover:bg-primary/10"
              >
                <div>
                  <h2 className="font-heading text-lg font-semibold text-foreground">
                    Bekijk de voorwaarden via Dordt Isoleert
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    energiehulp.nl/dordrecht/isoleren
                  </p>
                </div>
                <ExternalLink
                  className="h-5 w-5 shrink-0 text-primary transition-transform group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </a>
            </div>
          </div>
        </div>
      </div>

      <CtaBand />
    </>
  );
}
